import { useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { PracticeLearnerContext, type PracticeInstrumentChoice } from "./practiceInstrument.js";
import { readPracticeToolContext, toolContextNote } from "./practiceToolContext.js";

/**
 * Shown by a practice tool that was opened from an assigned task: a way back to
 * the task, and a plain note when the learner or instrument in the link could
 * not be honoured. It never starts, saves or changes anything.
 */
export function PracticeToolContextNote({ choice, selectedLearner }: { choice: PracticeInstrumentChoice; selectedLearner?: string | null }) {
  const { search } = useLocation();
  const shellLearner = useContext(PracticeLearnerContext);
  const context = readPracticeToolContext(search);
  if (!context.todo) return null;
  const note = toolContextNote(context, {
    selectedLearner: selectedLearner === undefined ? shellLearner : selectedLearner,
    instruments: choice.instruments,
    loading: choice.loading,
    selectedInstrument: choice.value,
    // The shell removes `student` once it has switched learner; until then the selection may still be the old one.
    switchPending: new URLSearchParams(search).has("student"),
  });
  return (
    <div className="stack practice-tool-context">
      {context.returnTo && (
        <p className="small">
          <Link to={context.returnTo}>Back to your task</Link>
        </p>
      )}
      {note === "learner-differs" && (
        <p className="small muted" role="status">
          This link was made for a different learner, so nothing was preselected. Check who is selected before you start.
        </p>
      )}
      {note === "instrument-differs" && (
        <p className="small muted" role="status">
          This task is for {context.instrument}, but {choice.value ? choice.value : "no instrument"} is selected. Check the instrument before you start.
        </p>
      )}
      {note === "instrument-unconfirmed" && (
        <p className="small muted" role="status">
          This task doesn’t say which instrument it is for, so none was preselected from it. Check the instrument before you start.
        </p>
      )}
    </div>
  );
}
